import React, { useContext, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { AuthContext } from '../../contexts/AuthContext';
import { AuthObject } from '../../utils/frontEndTypes';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useActiveTenant } from '../../contexts/ActiveTenantContext';
import { useConfig } from '../../contexts/ConfigContext';
import { usePendingInvites } from '../../hooks/usePendingInvites';
import TenantSwitcher from '../tenantSwitcher/TenantSwitcher';
import DarkModeToggle from '../ui/DarkModeToggle';

import LogoColor from '../../assets/images/archieWordmark.svg';
import LogoDark from '../../assets/images/archieWordmarkDark.svg';
import { signedFetch } from '../../utils/signedFetch';

import { SidebarLayout } from '../ui/sidebar-layout';
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer
} from '../ui/sidebar';
import { Navbar, NavbarItem, NavbarSection, NavbarSpacer } from '../ui/navbar';
import { Dropdown, DropdownButton, DropdownDivider, DropdownItem, DropdownLabel, DropdownMenu } from '../ui/dropdown';
import { Avatar } from '../ui/avatar';

import { ArrowRightStartOnRectangleIcon, ChevronUpIcon, ChevronUpDownIcon } from '@heroicons/react/16/solid';
import {
  ChatBubbleLeftRightIcon,
  BookOpenIcon,
  CpuChipIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  EnvelopeIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/20/solid';

function UserMenuItems({
  pendingCount,
  handleLogout
}: {
  pendingCount: number;
  handleLogout: (e: React.MouseEvent) => void;
}) {
  return (
    <>
      <DropdownItem href="/user/profile">
        <UserCircleIcon />
        <DropdownLabel>User Profile</DropdownLabel>
      </DropdownItem>
      {pendingCount > 0 && (
        <DropdownItem href="/user/pending-invitations">
          <EnvelopeIcon />
          <DropdownLabel>Pending Invitations ({pendingCount})</DropdownLabel>
        </DropdownItem>
      )}
      <DropdownDivider />
      <DropdownItem href="/" onClick={handleLogout}>
        <ArrowRightStartOnRectangleIcon />
        <DropdownLabel>Log out</DropdownLabel>
      </DropdownItem>
    </>
  );
}

const ApplicationLayout: React.FC = () => {
  const auth = useContext(AuthContext) as AuthObject;
  const location = useLocation();
  const { isSiteAdmin, hasTenantAccess, isTenantOwner, isTenantAdmin } = usePermissions();
  const { availableTenants, activeTenant } = useActiveTenant();
  const { config } = useConfig();
  const { pendingCount } = usePendingInvites();

  const [isTenantSwitcherOpen, setIsTenantSwitcherOpen] = useState(false);

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await signedFetch(`${config.api.apiUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch {
      // Ignore logout errors
    }
    window.location.href = `${config.api.apiUrl}/logout`;
  };

  const isCurrent = (path: string, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <>
      <SidebarLayout
        navbar={
          <Navbar>
            <NavbarSpacer />
            <NavbarSection>
              <Dropdown>
                <DropdownButton as={NavbarItem}>
                  <Avatar
                    initials={auth.user?.fullName?.charAt(0) || 'U'}
                    className="size-6 bg-primary-600 text-white"
                    square
                  />
                </DropdownButton>
                <DropdownMenu className="min-w-64" anchor="bottom end">
                  <UserMenuItems pendingCount={pendingCount} handleLogout={handleLogout} />
                </DropdownMenu>
              </Dropdown>
            </NavbarSection>
          </Navbar>
        }
        sidebar={
          <Sidebar>
            <SidebarHeader>
              <SidebarItem href="/">
                <img className="h-6 dark:hidden" src={LogoColor} alt="Archie" />
                <img className="h-6 hidden dark:block" src={LogoDark} alt="Archie" />
              </SidebarItem>
            </SidebarHeader>

            <SidebarBody>
              {hasTenantAccess && (
                <SidebarSection>
                  <SidebarItem href="/chat-history" current={isCurrent('/chat-history')}>
                    <ChatBubbleLeftRightIcon />
                    <SidebarLabel>Chat</SidebarLabel>
                  </SidebarItem>
                </SidebarSection>
              )}

              {hasTenantAccess && (
                <SidebarSection>
                  {availableTenants.length > 1 ? (
                    <SidebarHeading>
                      <button
                        onClick={() => setIsTenantSwitcherOpen(true)}
                        className="flex w-full cursor-pointer items-center justify-between rounded-md px-1 -mx-1 py-0.5 hover:bg-(--color-surface-hover) transition-colors"
                        title="Switch Organization"
                      >
                        <span className="truncate">{activeTenant?.tenantName || 'Organization'}</span>
                        <ChevronUpDownIcon className="size-4 shrink-0 text-(--color-text-muted)" />
                      </button>
                    </SidebarHeading>
                  ) : (
                    <SidebarHeading>{activeTenant?.tenantName || 'Organization'}</SidebarHeading>
                  )}
                  <SidebarItem href="/organization/members" current={isCurrent('/organization/members')}>
                    <UserGroupIcon />
                    <SidebarLabel>Members</SidebarLabel>
                  </SidebarItem>
                  <SidebarItem
                    href="/organization/knowledge-bases"
                    current={isCurrent('/organization/knowledge-bases')}
                  >
                    <BookOpenIcon />
                    <SidebarLabel>Knowledge Bases</SidebarLabel>
                  </SidebarItem>
                  <SidebarItem href="/organization/assistants" current={isCurrent('/organization/assistants')}>
                    <CpuChipIcon />
                    <SidebarLabel>Assistants</SidebarLabel>
                  </SidebarItem>
                  {(isTenantOwner || isTenantAdmin) && (
                    <SidebarItem href="/organization/tools/servers" current={isCurrent('/organization/tools/servers')}>
                      <WrenchScrewdriverIcon />
                      <SidebarLabel>MCP Servers</SidebarLabel>
                    </SidebarItem>
                  )}
                  {isTenantOwner && (
                    <SidebarItem href="/organization/settings" current={isCurrent('/organization/settings')}>
                      <Cog6ToothIcon />
                      <SidebarLabel>Settings</SidebarLabel>
                    </SidebarItem>
                  )}
                </SidebarSection>
              )}

              {isSiteAdmin && (
                <SidebarSection>
                  <SidebarHeading>Admin</SidebarHeading>
                  <SidebarItem href="/admin/organizations" current={isCurrent('/admin/organizations')}>
                    <ShieldCheckIcon />
                    <SidebarLabel>Organizations</SidebarLabel>
                  </SidebarItem>
                </SidebarSection>
              )}

              <SidebarSpacer />

              <SidebarSection>
                <div className="px-2">
                  <DarkModeToggle />
                </div>
              </SidebarSection>
            </SidebarBody>

            <SidebarFooter className="max-lg:hidden">
              <Dropdown>
                <DropdownButton as={SidebarItem}>
                  <span className="flex min-w-0 items-center gap-3">
                    <Avatar
                      initials={auth.user?.fullName?.charAt(0) || 'U'}
                      className="size-10 bg-primary-600 text-white"
                      square
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm/5 font-medium text-(--color-text-primary)">
                        {auth.user?.fullName || 'User'}
                      </span>
                    </span>
                  </span>
                  <ChevronUpIcon />
                </DropdownButton>
                <DropdownMenu className="min-w-64" anchor="top start">
                  <UserMenuItems pendingCount={pendingCount} handleLogout={handleLogout} />
                </DropdownMenu>
              </Dropdown>
            </SidebarFooter>
          </Sidebar>
        }
      >
        <Outlet />
      </SidebarLayout>

      <TenantSwitcher isOpen={isTenantSwitcherOpen} onClose={() => setIsTenantSwitcherOpen(false)} />
    </>
  );
};

export default ApplicationLayout;
